import type {
  ProblemOccurrence,
  ProblemTask,
  ProblemTaskGroup,
  AgentPriority
} from '@axilon-apps/domains-ontology-ui-state'
export type WorkStatus = 'open' | 'done' | 'rejected'
export function occurrenceLocation(occurrence: ProblemOccurrence): string | null {
  const locations = new Set(
    occurrence.affected_entities.map((entity) => entity.location.trim()).filter(Boolean)
  )
  return locations.size === 1 ? [...locations][0] : null
}
export function groupOccurrences<T>(
  items: readonly T[],
  occurrenceOf: (item: T) => ProblemOccurrence
): { location: string | null; items: T[] }[] {
  const locations = new Map<string | null, T[]>()
  for (const item of items) {
    const location = occurrenceLocation(occurrenceOf(item))
    locations.set(location, [...(locations.get(location) ?? []), item])
  }
  return [...locations]
    .map(([location, rows]) => ({
      location,
      items: rows.sort((a, b) => {
        const left = occurrenceOf(a),
          right = occurrenceOf(b)
        return right.opened_at.localeCompare(left.opened_at) || left.id.localeCompare(right.id)
      })
    }))
    .sort((a, b) =>
      a.location === null
        ? b.location === null
          ? 0
          : 1
        : b.location === null
          ? -1
          : a.location.localeCompare(b.location, undefined, { numeric: true })
    )
}
export const problemTaskStatus = (task: ProblemTask): WorkStatus =>
  task.approval_status === 'rejected' ? 'rejected' : task.work_status
export function visibleProblemTasks(
  tasks: readonly ProblemTask[],
  status: WorkStatus,
  retained = false
): ProblemTask[] {
  return tasks
    .filter(
      (task) =>
        !task.retired_at &&
        (problemTaskStatus(task) === status ||
          (status === 'open' && problemTaskStatus(task) === 'done') ||
          (retained && status === 'done' && problemTaskStatus(task) === 'open'))
    )
    .sort((a, b) => a.sequence - b.sequence || a.id.localeCompare(b.id))
}
export function visibleTaskGroups(
  groups: readonly ProblemTaskGroup[],
  status: WorkStatus,
  retained: ReadonlySet<string>
): ProblemTaskGroup[] {
  return groups.filter(
    (group) =>
      group.counts[status] > 0 ||
      (retained.has(group.occurrence.id) && group.counts.open + group.counts.done > 0)
  )
}
export function workPriority(value: string | null | undefined): AgentPriority | null {
  return value === 'critical' || value === 'high' || value === 'medium' || value === 'low'
    ? value
    : null
}
export const occurrenceContext = (occurrence: ProblemOccurrence) => ({
  source: 'occurrence' as const,
  occurrenceId: occurrence.id,
  conceptId: occurrence.problem_id
})
export function workMutationError(error: unknown): string {
  return error && typeof error === 'object' && 'status' in error && error.status === 409
    ? 'This work changed. Refresh and try again.'
    : "Couldn't save the change. Try again."
}
